// components/Skills.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaDesktop, FaDatabase, FaTools, FaBrain } from 'react-icons/fa';

const SkillCategory = ({ icon, title, skills }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center mb-4">
        <div className="text-primary mr-3">{icon}</div>
        <h3 className="text-xl font-bold">{title}</h3>
      </div>
      <ul className="space-y-2">
        {skills.map((skill, index) => (
          <li key={index} className="flex items-center">
            <div className="w-full">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">{skill.name}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary rounded-full h-2" 
                  style={{ width: `${skill.level}%` }}
                ></div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Skills = () => {
  const skillCategories = [
    {
      icon: <FaCode size={24} />,
      title: "Programming Languages",
      skills: [
        { name: "Python", level: 85 },
        { name: "Java", level: 80 },
        { name: "JavaScript", level: 75 },
        { name: "Dart", level: 70 },
      ]
    },
    {
      icon: <FaDesktop size={24} />,
      title: "Web Development",
      skills: [
        { name: "HTML", level: 90 },
        { name: "CSS", level: 85 },
        { name: "React.js", level: 75 },
      ]
    },
    {
      icon: <FaTools size={24} />,
      title: "Frameworks",
      skills: [
        { name: "Spring Boot", level: 70 },
        { name: "React.js", level: 75 },
        { name: "Flutter", level: 70 },
      ]
    },
    {
      icon: <FaDatabase size={24} />,
      title: "Backend & Databases",
      skills: [
        { name: "Node.js", level: 75 },
        { name: "MySQL", level: 80 },
        { name: "MongoDB", level: 70 },
      ]
    },
    {
      icon: <FaTools size={24} />,
      title: "Dev Tools & Platforms",
      skills: [
        { name: "Git", level: 85 },
        { name: "Postman", level: 80 },
        { name: "Figma", level: 70 },
      ]
    },
    {
      icon: <FaBrain size={24} />,
      title: "Soft Skills",
      skills: [
        { name: "Problem Solving", level: 90 },
        { name: "Team Collaboration", level: 85 },
        { name: "Communication", level: 80 },
      ]
    },
  ];

  return (
    <section id="skills" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-2">My Skills</h2>
          <div className="w-24 h-1 bg-primary mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Here are my technical and soft skills that I've developed through coursework, projects, and self-learning.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCategories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <SkillCategory 
                icon={category.icon} 
                title={category.title} 
                skills={category.skills} 
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;